/**
 * CommandCard — the single-account hero surface for TodayView.
 */

import React from 'react'
import {
  Flame,
  Users,
  ArrowUpRight,
  Clock,
  TrendingUp,
  MessageSquare,
  FileText,
  BellOff,
} from 'lucide-react'
import { HeatThermometer } from '@/components/TAG/HeatThermometer'
import { Button } from '@/components/UI/Button/Button'

export interface CommandCardProps {
  companyName: string
  tagScore: number
  trend: 'surging' | 'rising' | 'stable' | 'declining' | 'cold'
  velocity?: number
  priority?: 'P0' | 'P1' | 'P2'
  whyNow: string[]
  committee?: { activated: number; total: number; latestJoin?: string }
  warmPath?: { name: string; channel: string; note?: string }
  lastTouch?: { daysAgo: number; channel?: string }
  eyebrow?: string
  onOpenBriefing?: () => void
  onStartOutreach?: () => void
  onSnooze?: () => void
}

const PRIORITY_STYLE: Record<
  NonNullable<CommandCardProps['priority']>,
  { label: string; token: string }
> = {
  P0: { label: 'P0 · act today', token: 'var(--state-danger)' },
  P1: { label: 'P1 · this week', token: 'var(--state-warning)' },
  P2: { label: 'P2 · nurture', token: 'var(--state-info)' },
}

export const CommandCard: React.FC<CommandCardProps> = ({
  companyName,
  tagScore,
  trend,
  velocity,
  priority,
  whyNow,
  committee,
  warmPath,
  lastTouch,
  eyebrow = '#1 priority right now',
  onOpenBriefing,
  onStartOutreach,
  onSnooze,
}) => {
  const headingId = React.useId()
  const isSurging = trend === 'surging'
  const priorityStyle = priority ? PRIORITY_STYLE[priority] : null

  return (
    <article
      aria-labelledby={headingId}
      className="relative overflow-hidden rounded-[var(--radius-xl)] border border-[var(--border-accent)] bg-[var(--bg-card)]"
      style={{
        boxShadow: isSurging
          ? 'var(--shadow-md), var(--glow-brand-strong)'
          : 'var(--shadow-md)',
      }}
    >
      {/* Orb glow — only on surging, carries landing-page visual language into the app shell */}
      {isSurging && (
        <>
          <div
            aria-hidden
            className="absolute -top-24 -right-24 h-72 w-72 rounded-full pointer-events-none"
            style={{ background: 'var(--orb-accent-bg)', filter: 'blur(var(--orb-blur))' }}
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -left-24 h-56 w-56 rounded-full pointer-events-none"
            style={{ background: 'var(--orb-purple-bg)', filter: 'blur(var(--orb-blur))' }}
          />
        </>
      )}

      <div className="relative z-10 p-6 sm:p-8">
        {/* Eyebrow */}
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[var(--tracking-label)] text-[var(--text-muted)]">
          <Flame className="h-3.5 w-3.5" style={{ color: 'var(--accent)' }} />
          <span>{eyebrow}</span>
          {priorityStyle && (
            <span
              className="ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold normal-case tracking-normal"
              style={{
                backgroundColor: `color-mix(in srgb, ${priorityStyle.token} 12%, transparent)`,
                color: priorityStyle.token,
                border: `1px solid color-mix(in srgb, ${priorityStyle.token} 35%, transparent)`,
              }}
            >
              {priorityStyle.label}
            </span>
          )}
        </div>

        {/* Header row: thermometer + identity */}
        <header className="mt-4 flex items-start gap-5">
          <HeatThermometer score={tagScore} trend={trend} size="lg" showLabel={false} />
          <div className="min-w-0 flex-1">
            <h2
              id={headingId}
              className="truncate text-[28px] font-bold leading-tight text-[var(--text-primary)]"
            >
              {companyName}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[var(--text-muted)]">
              <span className="inline-flex items-center gap-1.5">
                <TrendingUp
                  className="h-3.5 w-3.5"
                  style={{ color: isSurging ? 'var(--accent)' : 'var(--text-muted)' }}
                />
                <span
                  className="font-semibold"
                  style={{ color: isSurging ? 'var(--accent)' : 'var(--text-secondary)' }}
                >
                  {velocity != null ? `+${velocity} pts/day` : trend}
                </span>
                {isSurging && <span className="text-[var(--text-muted)]">· surging</span>}
              </span>
              <span aria-hidden>·</span>
              <span>TAG {tagScore}/100</span>
            </div>
          </div>
        </header>

        {/* Why now — the agent's reasoning, not chrome */}
        {whyNow.length > 0 && (
          <section aria-label="Why now" className="mt-6">
            <div className="text-[11px] font-semibold uppercase tracking-[var(--tracking-label)] text-[var(--text-subtle)]">
              Why now
            </div>
            <ul className="mt-2 space-y-1.5">
              {whyNow.slice(0, 3).map((reason, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[15px] leading-snug text-[var(--text-primary)]"
                >
                  <span
                    className="mt-2 h-1 w-1 flex-shrink-0 rounded-full"
                    style={{ background: 'var(--accent)' }}
                    aria-hidden
                  />
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Intel grid — 4 quick facts */}
        <section
          aria-label="Account intel"
          className="mt-6 grid grid-cols-2 gap-px rounded-[var(--radius-md)] overflow-hidden border border-[var(--border-accent)] bg-[var(--border-accent)] sm:grid-cols-4"
        >
          <IntelCell
            icon={<Users className="h-3.5 w-3.5" />}
            label="Committee"
            value={committee ? `${committee.activated} of ${committee.total}` : '—'}
            sub={committee?.latestJoin}
          />
          <IntelCell
            icon={<ArrowUpRight className="h-3.5 w-3.5" />}
            label="Warm path"
            value={warmPath ? warmPath.name : 'No warm path'}
            sub={warmPath ? `via ${warmPath.channel}` : undefined}
            muted={!warmPath}
          />
          <IntelCell
            icon={<Clock className="h-3.5 w-3.5" />}
            label="Last touch"
            value={lastTouch ? `${lastTouch.daysAgo}d ago` : 'Never'}
            sub={lastTouch?.channel}
            muted={!lastTouch}
          />
          <IntelCell
            icon={<TrendingUp className="h-3.5 w-3.5" />}
            label="Velocity"
            value={velocity != null ? `+${velocity}` : '—'}
            sub={velocity != null ? 'pts/day' : undefined}
            accent={isSurging}
          />
        </section>

        {/* Actions */}
        <footer className="mt-6 flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            size="lg"
            icon={<MessageSquare className="h-4 w-4" />}
            onClick={onStartOutreach}
          >
            Start outreach
          </Button>
          {onOpenBriefing && (
            <Button
              variant="secondary"
              size="lg"
              icon={<FileText className="h-4 w-4" />}
              onClick={onOpenBriefing}
            >
              Open briefing
            </Button>
          )}
          <Button
            variant="ghost"
            size="lg"
            icon={<BellOff className="h-4 w-4" />}
            onClick={onSnooze}
            className="ml-auto"
          >
            Snooze 2h
          </Button>
        </footer>
      </div>
    </article>
  )
}

interface IntelCellProps {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  muted?: boolean
  accent?: boolean
}

const IntelCell: React.FC<IntelCellProps> = ({ icon, label, value, sub, muted, accent }) => (
  <div className="bg-[var(--bg-card)] p-3">
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[var(--tracking-label)] text-[var(--text-subtle)]">
      <span style={{ color: accent ? 'var(--accent)' : 'var(--text-subtle)' }}>{icon}</span>
      <span>{label}</span>
    </div>
    <div
      className="mt-1 truncate text-[15px] font-semibold"
      style={{
        color: muted
          ? 'var(--text-muted)'
          : accent
          ? 'var(--accent)'
          : 'var(--text-primary)',
      }}
      title={value}
    >
      {value}
    </div>
    {sub && (
      <div className="truncate text-xs text-[var(--text-muted)]" title={sub}>
        {sub}
      </div>
    )}
  </div>
)

export default CommandCard
